import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/components/auth-context'

// Leer versión de variable de entorno
// Versión de la aplicación
const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || '1.9.4'

interface AppUpdate {
    id: string
    version: string
    title: string
    description: string
    type: 'feature' | 'bugfix' | 'breaking' | 'security'
    is_important: boolean
    released_at: string
    created_at: string
}

// Global state to prevent multiple components from triggering the same check/insert logic simultaneously
let globalCheckInProgress = false;
let globalCheckFinished = false;

export function useAppVersion() {
    const { user } = useAuth()
    const [hasUpdate, setHasUpdate] = useState(false)
    const [updateInfo, setUpdateInfo] = useState<AppUpdate | null>(null)
    const [isChecking, setIsChecking] = useState(true)

    useEffect(() => {
        let isMounted = true

        if (!user?.id) {
            setIsChecking(false)
            return
        }

        const checkVersion = async () => {
            // If already finished or in progress in another instance of this hook,
            // we skip the side-effect (the notification creation) but still set the state if needed.
            // However, we want to know if there's an update.
            
            try {
                // 1. Leer versión guardada en localStorage
                const lastSeenVersion = localStorage.getItem('app_version')

                console.log(`[useAppVersion] Checking version. Current: ${CURRENT_VERSION}, Last seen: ${lastSeenVersion}`)

                // 2. Si es la misma, no hacer nada más que marcar como no cargando
                if (lastSeenVersion === CURRENT_VERSION) {
                    console.log('[useAppVersion] Already on latest seen version.')
                    if (isMounted) setIsChecking(false)
                    return
                }

                // Race condition guard: only one hook instance performs the DB search/insert
                if (globalCheckInProgress || globalCheckFinished) {
                    if (isMounted) setIsChecking(false)
                    return
                }

                globalCheckInProgress = true

                if (!isMounted) {
                    globalCheckInProgress = false
                    return
                }

                // 3. Buscar información de la actualización
                const { data: update, error } = await supabase
                    .from('app_updates')
                    .select('*')
                    .eq('version', CURRENT_VERSION)
                    .maybeSingle()

                if (error) {
                    console.error('[useAppVersion] Error fetching update info:', error)
                    globalCheckInProgress = false
                    if (isMounted) setIsChecking(false)
                    return
                }

                if (!update) {
                    console.warn(`[useAppVersion] No update info found for version ${CURRENT_VERSION}. Marking as seen to stop loops.`)
                    localStorage.setItem('app_version', CURRENT_VERSION)
                    globalCheckInProgress = false
                    globalCheckFinished = true
                    if (isMounted) setIsChecking(false)
                    return
                }

                if (!isMounted) {
                    globalCheckInProgress = false
                    return
                }

                if (update.is_important) {
                    // 4. Verificar si ya creamos esta notificación específica en la DB
                    // Usamos un título muy específico para evitar duplicados
                    const notifTitle = `🚀 Update ${CURRENT_VERSION}: ${update.title}`
                    
                    const { data: existingNotif } = await supabase
                        .from('notifications')
                        .select('id')
                        .eq('user_id', user.id)
                        .eq('title', notifTitle)
                        .maybeSingle()

                    if (!existingNotif) {
                        console.log(`[useAppVersion] Creating notification for version ${CURRENT_VERSION}`)
                        await supabase
                            .from('notifications')
                            .insert({
                                user_id: user.id,
                                title: notifTitle,
                                message: update.description,
                                type: 'success',
                                link: `/changelog/${CURRENT_VERSION}`,
                                is_read: false
                            })

                        if (isMounted) {
                            setHasUpdate(true)
                            setUpdateInfo(update)
                        }
                    } else {
                        console.log(`[useAppVersion] Notification already exists in DB for version ${CURRENT_VERSION}. Skipping modal.`)
                        // If it's already in the DB, we don't show the modal again to avoid annoyance across devices,
                        // but we still want to mark this browser's version as seen in localStorage below.
                    }
                }

                // 5. Marcamos como vista para que no vuelva a saltar el toast en esta sesión/browser
                localStorage.setItem('app_version', CURRENT_VERSION)
                console.log('[useAppVersion] Success! Version marked as seen.')
                globalCheckFinished = true
            } catch (error) {
                console.error('Error in version check:', error)
            } finally {
                globalCheckInProgress = false
                if (isMounted) setIsChecking(false)
            }
        }

        checkVersion()

        return () => {
            isMounted = false
        }
    }, [user?.id])

    return {
        hasUpdate,
        updateInfo,
        currentVersion: CURRENT_VERSION,
        isChecking
    }
}
